import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  input,
  computed,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { AIRiskLevel, DpiaOverallRisk } from '../../constants/assessment.constants';
import { RiskMeterComponent } from '../risk-meter/risk-meter';
import { EmptyStateComponent } from '../empty-state/empty-state';
import {
  RiskMeterDescriptor,
  aiRiskLevelToMeter,
  dpiaOverallRiskToMeter,
  riskLevelToMeter,
} from '../../utils/risk-meter.utils';
import type {
  AiRiskProfileData,
  DpiaRiskProfileData,
  AnyRiskProfileData,
} from '../../models/assessments';
import { isAiRiskProfile, isDpiaRiskProfile } from '../../models/assessments/any-risk-profile';
import { normalizeAiRiskProfile } from '../../utils/assessment.utils';

/** Union accepted by the display. The `AssessmentRecord.risk_profile`
 *  field is the AI-Risk arm at wire level (either the current
 *  `sections` shape or the legacy flat shape — `normalizeAiRiskProfile`
 *  folds both); DPIA rows carry the DPIA arm at runtime and are
 *  narrowed here before the AI-Risk normaliser runs. */
type RiskDisplayProfile = AiRiskProfileData | DpiaRiskProfileData;

interface MeterChip extends RiskMeterDescriptor {
  /** Compact name shown inside the chip when the inline mode renders
   *  the chip with its prefix ("GDPR", "Cyber", "AI Act"). */
  prefix: string;
  /** Full cell-header label used by the card-mode grid
   *  ("GDPR Risk", "Cybersecurity Risk", "AI Act Risk"). */
  columnLabel: string;
  /** Marks a chip as the "primary axis" for the assessment type — the
   *  inline mode promotes it ahead of higher-severity siblings. Used
   *  by AI Risk assessments where AI Act classification is the lede
   *  regardless of how GDPR / Cyber compare numerically. */
  primary?: boolean;
}

/**
 * Assessment risk display — renders 2–3 risk-meter chips for the
 * GDPR / Cybersecurity / AI-Act scores on a `RiskProfile`.
 *
 * Two modes, deliberately different layouts:
 *   - `card`   — 3-column grid. Each cell has a "{Framework} Risk"
 *                header at top and a *bare* meter chip below (no
 *                prefix inside the chip — the cell header carries the
 *                category name). Sits inside a card body section so
 *                the three scores read as a stat strip.
 *   - `inline` — Vertical stack of meter chips with the prefix shown
 *                *inside* the chip ("GDPR · High"). Tuned for narrow
 *                table cells in the assessment list view where a
 *                horizontal grid wouldn't fit.
 *
 * Scale → meter mapping is owned by `utils/risk-meter.utils.ts` and
 * shared with the drawer's risk-profile tab so both surfaces stay
 * lockstep.
 */
@Component({
  selector: 'app-risk-score-display',
  imports: [
    RiskMeterComponent,
    EmptyStateComponent,
    TranslocoModule,
    MatTooltipModule,
    MatIconModule,
  ],
  templateUrl: './risk-score-display.html',
  styleUrl: './risk-score-display.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.inline-mode]': "mode() === 'inline'",
  },
})
export class RiskScoreDisplayComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationVersion = signal(0);

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));
  }

  /** The risk profile data to display. Accepts either the AI-Risk shape
   *  (three axes) or the DPIA shape (Overall + Residual + Art. 36); the
   *  component narrows at runtime via `isDpiaRiskProfile` and picks the
   *  right template branch. */
  riskProfile = input<RiskDisplayProfile | null | undefined>(null);

  /** Display mode: 'card' shows the 3-column stat strip; 'inline'
   *  shows a vertical stack of chips for table cells. */
  mode = input<'card' | 'inline'>('card');

  /** Primary line of the empty state. Shown when no risk profile is
   *  attached to the assessment yet. */
  emptyText = input<string | null>(null);

  /** Secondary hint shown below `emptyText` in card mode. Pass an
   *  empty string to suppress it. Hidden automatically in inline
   *  mode (table cells are too tight for two lines). */
  emptyHint = input<string | null>(null);

  /** Resolved empty-state primary line. Falls back to the
   *  default translation key when the caller hasn't passed an override. */
  readonly resolvedEmptyText = computed(() => {
    this.translationVersion();
    return this.emptyText() ?? this.transloco.translate<string>('risk_score.empty_text_default');
  });

  /** Resolved empty-state hint. `null` is treated as "use default";
   *  an explicit empty string suppresses the hint. */
  readonly resolvedEmptyHint = computed(() => {
    this.translationVersion();
    const v = this.emptyHint();
    if (v === '') return '';
    return v ?? this.transloco.translate<string>('risk_score.empty_hint_default');
  });

  /** The input profile widened to the discriminated-union type the
   *  DPIA guard expects. `AssessmentRecord.risk_profile` is typed as
   *  the AI-Risk shape upstream (no `kind` discriminator) — DPIA rows
   *  carry `DpiaRiskProfileData` at runtime. One `as unknown` at the
   *  input boundary lets every downstream `isDpiaRiskProfile` call
   *  flow through TypeScript's narrowing cleanly. */
  private readonly narrowedProfile = computed<AnyRiskProfileData | null>(() => {
    const p = this.riskProfile();
    return (p as unknown as AnyRiskProfileData | null) ?? null;
  });

  /** True when the profile is DPIA-shaped. Drives the template's outer
   *  branch (DPIA gets its own Overall + Residual + Art.36 strip). */
  protected readonly isDpia = computed(() => isDpiaRiskProfile(this.narrowedProfile()));

  /** DPIA-shaped profile, narrowed for the template. */
  protected readonly dpiaProfile = computed<DpiaRiskProfileData | null>(() => {
    const p = this.narrowedProfile();
    return isDpiaRiskProfile(p) ? p : null;
  });

  /** DPIA chip pair — Overall + (optional) Residual. Empty when the
   *  DPIA has no overall risk assigned yet (e.g. drafts before Step 5). */
  protected readonly dpiaChips = computed<{
    overall: MeterChip;
    residual: MeterChip | null;
  } | null>(() => {
    this.translationVersion();
    const p = this.dpiaProfile();
    if (!p || !p.overall_risk) return null;
    const overallPrefix = this.transloco.translate<string>('assessment.risk.dpia_overall_prefix');
    const residualPrefix = this.transloco.translate<string>('assessment.risk.dpia_residual_prefix');
    return {
      overall: {
        prefix: overallPrefix,
        columnLabel: overallPrefix,
        ...dpiaOverallRiskToMeter(p.overall_risk),
      },
      residual: p.residual_overall_risk
        ? {
            prefix: residualPrefix,
            columnLabel: residualPrefix,
            ...dpiaOverallRiskToMeter(p.residual_overall_risk),
          }
        : null,
    };
  });

  /** True when we should render the Art. 36 "prior consultation" pill
   *  (spec §7 Step 7): the DPIA has requested it AND residual overall
   *  is still HIGH. */
  protected readonly showArt36Pill = computed(() => {
    const p = this.dpiaProfile();
    if (!p) return false;
    if (!p.prior_consultation_required) return false;
    return p.residual_overall_risk === DpiaOverallRisk.HIGH;
  });

  /** Art.36 pill copy — resolved reactively on locale switch. */
  protected readonly art36PillLabel = computed(() => {
    this.translationVersion();
    return this.transloco.translate<string>('assessment.risk.dpia_art36_pill');
  });

  protected readonly art36PillTooltip = computed(() => {
    this.translationVersion();
    return this.transloco.translate<string>('assessment.risk.dpia_art36_tooltip');
  });

  /** Builds the AI-Risk meter-chip list. Each scored dimension contributes
   *  one chip; AI Act is included only when the backend has classified it
   *  as something other than UNKNOWN (the normaliser coerces missing
   *  scores to UNKNOWN so downstream never sees `undefined`).
   *  Returns `null` for DPIA-shaped profiles (the DPIA branch owns render). */
  scores = computed<MeterChip[] | null>(() => {
    this.translationVersion();
    const raw = this.narrowedProfile();
    // Only the AI-Risk arm of the union owns the GDPR / Cyber / AI-Act
    // triplet the meter chips render. DPIA has its own branch above,
    // and TIA carries a separate risk model rendered elsewhere — skip
    // both here rather than casting past them.
    if (!raw || !isAiRiskProfile(raw)) return null;
    const airisk = normalizeAiRiskProfile(raw);
    if (!airisk) return null;

    const chips: MeterChip[] = [
      {
        prefix: this.transloco.translate<string>('risk_score.gdpr_prefix'),
        columnLabel: this.transloco.translate<string>('risk_score.gdpr_risk_column'),
        ...riskLevelToMeter(airisk.gdprScore),
      },
      {
        prefix: this.transloco.translate<string>('risk_score.cyber_prefix'),
        columnLabel: this.transloco.translate<string>('risk_score.cybersecurity_risk_column'),
        ...riskLevelToMeter(airisk.cybersecurityScore),
      },
    ];

    if (airisk.aiActClassification && airisk.aiActClassification !== AIRiskLevel.UNKNOWN) {
      chips.push({
        prefix: this.transloco.translate<string>('risk_score.ai_act_prefix'),
        columnLabel: this.transloco.translate<string>('risk_score.ai_act_risk_column'),
        // AI Act classification is the lede for AI Risk assessments — its
        // presence on the profile is the signal "this is an AI assessment".
        // Mark it as primary so the inline summary chip surfaces it ahead
        // of GDPR / Cyber regardless of comparative severity.
        primary: true,
        ...aiRiskLevelToMeter(airisk.aiActClassification),
      });
    }

    return chips;
  });

  /** Primary chip surfaced by the inline summary. Two-tier rule:
   *
   *   1. If any chip is marked `primary: true` (AI Risk assessments
   *      mark AI Act this way), surface it regardless of severity —
   *      the assessment-type intent reads first.
   *   2. Otherwise, pick the highest-severity chip from the set.
   *
   *  Ties resolve to the first chip in insertion order. The `+N` pill
   *  and the breakdown tooltip still surface every other dimension. */
  protected readonly worstChip = computed<MeterChip | null>(() => {
    const chips = this.scores();
    if (!chips || chips.length === 0) return null;
    const primary = chips.find(c => c.primary);
    if (primary) return primary;
    let worst = chips[0];
    for (const c of chips) {
      if (c.level > worst.level) worst = c;
    }
    return worst;
  });

  /** Number of additional chips beyond the worst — drives the `+N`
   *  suffix on the inline summary so users see "there is more here"
   *  without growing the row. Hidden when there's only one dimension. */
  protected readonly extraDimensionCount = computed<number>(() => {
    const chips = this.scores();
    return chips && chips.length > 1 ? chips.length - 1 : 0;
  });

  /** Multi-line breakdown text for the inline-mode tooltip. Lists every
   *  dimension and its level so the full picture is one hover away
   *  while the cell stays a single line of chrome. */
  protected readonly breakdownTooltip = computed<string>(() => {
    const chips = this.scores();
    if (!chips || chips.length === 0) return '';
    return chips.map(c => `${c.prefix} · ${c.label}`).join('\n');
  });
}
